import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";

import { useLang } from "@/i18n/LanguageContext";
import {
  COMMISSION_CHAPTER_ORDER,
  COMMISSION_CHAPTER_LABELS,
  COMMISSION_STATUS_LABELS,
} from "@/data/projectChapters";

// -----------------------------------------------------------------------------
// Maison Dodji Paris — Private Commission editorial page.
//
// Route:   /private-commissions/:slug
// Access:  direct link only. noindex, nofollow. Not linked from navigation.
// Payload: /api/commission/:slug (server-side strips every private field and
//          only returns commissions with publiclyPublished === true).
//
// This page is independent from the confidential Project Space. It shows only
// what the Maison has explicitly authorised for public presentation.
// -----------------------------------------------------------------------------

function editorialDate(iso, lang) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(lang === "en" ? "en-GB" : "fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function PrivateCommission() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Always noindex, nofollow. This page must never be listed by search engines
  // regardless of publication state — the Maison decides visibility manually.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/commission/${encodeURIComponent(slug)}`);
        if (!cancelled) {
          if (r.ok) setData(await r.json());
        }
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Client-side OG/twitter meta injection (best-effort; social crawlers use the
  // dedicated worker branch which returns pre-rendered HTML).
  useEffect(() => {
    if (!data) return;
    const title = `${t.commission.openingHouse} — ${data.projectTitle || "Private Commission"}`;
    document.title = title;
    const nodes = [];
    const set = (attr, name, content) => {
      const m = document.createElement("meta");
      m.setAttribute(attr, name);
      m.setAttribute("content", content);
      document.head.appendChild(m);
      nodes.push(m);
    };
    set("name", "description", data.metaDescription || t.commission.awaitingBody);
    set("property", "og:type", "article");
    set("property", "og:site_name", t.commission.openingHouse);
    set("property", "og:title", title);
    set("property", "og:description", data.metaDescription || "");
    if (data.heroImage) {
      set("property", "og:image", data.heroImage);
      set("property", "og:image:alt", data.heroAlt || title);
    }
    set("name", "twitter:card", data.heroImage ? "summary_large_image" : "summary");
    set("name", "twitter:title", title);
    set("name", "twitter:description", data.metaDescription || "");
    if (data.heroImage) set("name", "twitter:image", data.heroImage);
    return () => { nodes.forEach((n) => { try { document.head.removeChild(n); } catch {} }); };
  }, [data, t]);

  if (loading) return null;
  if (!data) return <AwaitingPublication t={t.commission} />;

  const commissionLang = data.language === "en" ? "en" : "fr";
  const chapters = (data.commissionChapters || []).slice().sort(
    (a, b) => COMMISSION_CHAPTER_ORDER.indexOf(a.type) - COMMISSION_CHAPTER_ORDER.indexOf(b.type)
  );
  const chapterLabels = COMMISSION_CHAPTER_LABELS[commissionLang] || COMMISSION_CHAPTER_LABELS.fr;
  const statusLabel = data.status
    ? (COMMISSION_STATUS_LABELS[commissionLang] || COMMISSION_STATUS_LABELS.fr)[data.status] || ""
    : "";

  const c = t.commission;

  return (
    <div
      className="min-h-screen bg-[hsl(var(--md-chalk))] text-[hsl(var(--md-ink))]"
      data-testid="private-commission"
    >
      <div className="max-w-[820px] mx-auto px-6 md:px-12 py-16 md:py-24">
        {/* Discreet return link */}
        <div className="mb-16 md:mb-20">
          <Link
            to="/"
            className="font-eyebrow text-[hsl(var(--md-stone))] hover:text-[hsl(var(--md-ink))] transition-colors"
            data-testid="commission-return-maison"
          >
            {c.returnMaison}
          </Link>
        </div>

        {/* 1. Opening — four immutable lines */}
        <header className="mb-16 md:mb-24" data-testid="commission-opening">
          <p className="font-eyebrow text-[hsl(var(--md-stone))] tracking-[0.28em]">
            {c.openingHouse}
          </p>
          <p className="font-eyebrow text-[hsl(var(--md-stone))] mt-2 tracking-[0.28em]">
            {c.openingSeries}
          </p>
          <h1
            className="font-display text-[1.85rem] md:text-[2.5rem] leading-[1.15] text-[hsl(var(--md-ink))] mt-8"
            data-testid="commission-title"
          >
            {data.projectTitle}
          </h1>
          <p className="font-eyebrow text-[hsl(var(--md-stone))] mt-6 tracking-[0.28em]">
            {c.oneOfOne}
          </p>
        </header>

        {/* Hero image (only when the Maison has approved one) */}
        {data.heroImage && (
          <figure className="mb-16 md:mb-24" data-testid="commission-hero">
            <div className="relative aspect-[4/5] md:aspect-[3/4] bg-[hsl(var(--md-bone))] border border-[hsl(var(--md-line))] overflow-hidden">
              <img
                src={data.heroImage}
                alt={data.heroAlt || data.projectTitle}
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
                decoding="async"
              />
            </div>
          </figure>
        )}

        {/* 2. Editorial introduction */}
        {data.metaDescription && (
          <section className="mb-16 md:mb-24" data-testid="commission-introduction">
            <div className="chapter-hairline mb-8" />
            <p className="font-body text-[16px] md:text-[17px] leading-[1.85] text-[hsl(var(--md-ink))] whitespace-pre-line max-w-[62ch]">
              {data.metaDescription}
            </p>
          </section>
        )}

        {/* 3. Project status */}
        {statusLabel && (
          <section className="mb-16 md:mb-20" data-testid="commission-status">
            <p className="font-eyebrow text-[hsl(var(--md-stone))]">{c.statusEyebrow}</p>
            <p
              className="font-display italic text-[1.35rem] md:text-[1.5rem] text-[hsl(var(--md-ink))] mt-3"
              data-testid="commission-status-line"
            >
              {statusLabel}
            </p>
          </section>
        )}

        {/* 4. Project chapters */}
        {chapters.length > 0 && (
          <section className="mb-16 md:mb-24" data-testid="commission-chapters">
            <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-10">{c.chaptersHeading}</p>
            <div className="space-y-24 md:space-y-28">
              {chapters.map((chap, i) => (
                <CommissionChapter
                  key={i}
                  chap={chap}
                  label={chapterLabels[chap.type] || ""}
                  lang={commissionLang}
                  eyebrow={c.chapterEyebrow}
                />
              ))}
            </div>
          </section>
        )}

        {/* 6. Final presentation summary (piece + reference) */}
        {(data.pieceName || data.pieceNumber) && (
          <section className="mb-16 md:mb-24" data-testid="commission-piece">
            <div className="chapter-hairline mb-8" />
            <dl className="space-y-3">
              {data.pieceName && (
                <div className="grid grid-cols-12 gap-4">
                  <dt className="col-span-12 md:col-span-4 font-eyebrow text-[hsl(var(--md-stone))]">
                    {c.pieceLabel}
                  </dt>
                  <dd className="col-span-12 md:col-span-8 font-body text-[15px] text-[hsl(var(--md-ink))]">
                    {data.pieceName}
                  </dd>
                </div>
              )}
              {data.pieceNumber && (
                <div className="grid grid-cols-12 gap-4">
                  <dt className="col-span-12 md:col-span-4 font-eyebrow text-[hsl(var(--md-stone))]">
                    {c.pieceNumberLabel}
                  </dt>
                  <dd className="col-span-12 md:col-span-8 font-body text-[15px] text-[hsl(var(--md-ink))]">
                    {data.pieceNumber}
                  </dd>
                </div>
              )}
              <div className="grid grid-cols-12 gap-4">
                <dt className="col-span-12 md:col-span-4 font-eyebrow text-[hsl(var(--md-stone))]">
                  {c.oneOfOne}
                </dt>
                <dd className="col-span-12 md:col-span-8 font-body text-[15px] text-[hsl(var(--md-ink))]">
                  —
                </dd>
              </div>
            </dl>
          </section>
        )}

        {/* 7. Closing signature */}
        <footer className="mt-24 md:mt-28" data-testid="commission-closing">
          <div className="chapter-hairline mb-8" />
          <p className="font-eyebrow text-[hsl(var(--md-stone))] tracking-[0.28em]">
            {c.closingSignatureLine1}
          </p>
          <p className="font-display italic text-[1.25rem] md:text-[1.4rem] text-[hsl(var(--md-ink))] mt-3 leading-relaxed">
            {c.closingSignatureLine2}
            <br />
            {c.closingSignatureLine3}
          </p>
        </footer>
      </div>
    </div>
  );
}

function CommissionChapter({ chap, label, lang, eyebrow }) {
  const paragraphs = String(chap.body || "")
    .split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);
  return (
    <section data-testid={`commission-chapter-${chap.type}`}>
      <div className="chapter-hairline mb-8" />
      <p className="font-eyebrow text-[hsl(var(--md-stone))]">{eyebrow}</p>
      <h2 className="font-display text-[1.6rem] md:text-[2rem] leading-[1.15] text-[hsl(var(--md-ink))] mt-3 mb-6">
        {label}
      </h2>
      {chap.date && (
        <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-6">
          {editorialDate(chap.date, lang)}
        </p>
      )}
      {paragraphs.length > 0 && (
        <div className="space-y-5 font-body text-[16px] leading-[1.85] text-[hsl(var(--md-ink))] max-w-[62ch] whitespace-pre-line">
          {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      )}
      {chap.images && chap.images.length > 0 && (
        <div className="mt-10 space-y-10">
          {chap.images.map((im, i) => (
            im.url ? (
              <figure key={i}>
                <div className="relative aspect-[4/3] bg-[hsl(var(--md-bone))] border border-[hsl(var(--md-line))] overflow-hidden">
                  <img
                    src={im.url}
                    alt={im.caption || ""}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                {im.caption && (
                  <figcaption className="font-body text-[14px] text-[hsl(var(--md-stone))] mt-3">
                    {im.caption}
                  </figcaption>
                )}
              </figure>
            ) : null
          ))}
        </div>
      )}
      {chap.caption && !paragraphs.length && (
        <p className="font-body italic text-[15px] leading-[1.75] text-[hsl(var(--md-stone))] mt-6">
          {chap.caption}
        </p>
      )}
    </section>
  );
}

function AwaitingPublication({ t }) {
  return (
    <div
      className="min-h-screen bg-[hsl(var(--md-chalk))] text-[hsl(var(--md-ink))] flex items-center"
      data-testid="commission-awaiting"
    >
      <div className="max-w-[720px] mx-auto px-6 md:px-12 py-24 text-center">
        <p className="font-eyebrow text-[hsl(var(--md-stone))] tracking-[0.28em] mb-6">
          {t.openingHouse}
        </p>
        <h1 className="font-display text-[1.85rem] md:text-[2.35rem] leading-[1.15] mb-8">
          {t.awaitingTitle}
        </h1>
        <p className="font-body text-[16px] leading-[1.8] text-[hsl(var(--md-stone))] mb-12">
          {t.awaitingBody}
        </p>
        <Link
          to="/"
          className="font-eyebrow text-[hsl(var(--md-ink))] border-b border-[hsl(var(--md-line-strong))]"
        >
          {t.returnMaison}
        </Link>
      </div>
    </div>
  );
}
